// House-style date / datetime formatters. One pair, used everywhere
// so we don't surface three different date shapes on the same page.
//
// House style — British long form:
//   - date only:        "08 May 2026"            (DD Mon YYYY)
//   - date + time:      "08 May 2026, 14:30"     (no seconds, 24h)
//   - compact (no year): "08 May"                 (sparklines / x-axis labels)
//   - prose:            "8 May 2026"             (celebration banners only)
// All UK locale, all 24h. Slash form DD/MM/YYYY is off-spec — every
// date in the product should resolve to one of these helpers, not be
// formatted inline.
//
// Each helper accepts an ISO string, Date, or null. Null/invalid →
// the placeholder ('—' by default; pass a 2nd arg to override).

const safeDate = (v) => {
  if (v == null || v === '') return null;
  const d = v instanceof Date ? v : new Date(v);
  return isNaN(d.getTime()) ? null : d;
};

// Intl.DateTimeFormat in en-GB returns "Sept" (four letters) for the
// month: 'short' option ONLY for September — every other month is
// three letters. The 4-letter outlier breaks column alignment and
// drifts away from the documented house style of three-letter
// abbreviations everywhere. Normalise to "Sep" so the whole product
// renders one consistent abbreviation length.
const normaliseShortMonth = (s) => s.replace(/\bSept\b/g, 'Sep');

// Primary date format — "08 May 2026". 2-digit day so columns line up;
// 3-letter month so the eye doesn't have to parse a number twice.
export const fmtDate = (v, fallback = '—') => {
  const d = safeDate(v);
  if (!d) return fallback;
  return normaliseShortMonth(d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }));
};

// Date + time, comma-separated. Time is 24h with no seconds.
export const fmtDateTime = (v, fallback = '—') => {
  const d = safeDate(v);
  if (!d) return fallback;
  const date = normaliseShortMonth(d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }));
  const time = d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false });
  return `${date}, ${time}`;
};

// Compact date with no year — "08 May". Useful for chart axes, recent-
// activity strips, and other dense contexts where the year would
// dominate the cell. Avoid in tables where rows span more than 12
// months — the year carries real signal there.
export const fmtDateCompact = (v, fallback = '—') => {
  const d = safeDate(v);
  if (!d) return fallback;
  return normaliseShortMonth(d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' }));
};

// Natural-language long form — "8 May 2026" (no zero pad on day,
// full month name). Use sparingly: celebration banners, marketing-
// style headers, archive sentences. In data contexts prefer fmtDate
// so columns scan cleanly.
export const fmtDateLong = (v, fallback = '—') => {
  const d = safeDate(v);
  if (!d) return fallback;
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
};

// Range helpers — en dash "–" is the typographically correct range
// separator (em dash "—" is for prose interpolation, not ranges).
// Always route ranges through these helpers so a future tweak to the
// separator or the spacing applies app-wide. When start === end the
// helper collapses to a single rendered value rather than emitting
// a redundant "X – X" range; missing end gracefully falls back to
// the start; missing start returns the fallback.
//
// fmtDateRange     — "04 Sep 2026 – 06 Sep 2026"
// fmtDateTimeRange — "04 Sep 2026, 10:00 – 04 Sep 2026, 14:00"
export const fmtDateRange = (start, end, fallback = '—') => {
  const s = fmtDate(start, null);
  if (!s) return fallback;
  const e = fmtDate(end, null);
  if (!e || e === s) return s;
  return `${s} – ${e}`;
};
export const fmtDateTimeRange = (start, end, fallback = '—') => {
  const s = fmtDateTime(start, null);
  if (!s) return fallback;
  const e = fmtDateTime(end, null);
  if (!e || e === s) return s;
  return `${s} – ${e}`;
};

// Relative-time formatter for "last activity" / "X ago" columns.
// Returns British prose ("Today", "Yesterday", "3 days ago", "2 weeks
// ago", "1 month ago", "3 years ago") with correct singular / plural
// handling — fixes the "1 weeks ago" plural bug local helpers used to
// drift into. Use for low-resolution time-since contexts (suppliers
// list, dashboard activity); for high-resolution feeds (minutes/hours
// ago / "just now") keep the bespoke ComplianceOverview helper since
// it carries different requirements.
export const fmtRelativeDays = (v, fallback = null) => {
  const d = safeDate(v);
  if (!d) return fallback;
  const days = Math.floor((Date.now() - d.getTime()) / 86400000);
  if (days < 1) return 'Today';
  if (days < 2) return 'Yesterday';
  if (days < 7) return `${days} days ago`;
  if (days < 30) {
    const weeks = Math.floor(days / 7);
    return `${weeks} ${weeks === 1 ? 'week' : 'weeks'} ago`;
  }
  if (days < 365) {
    const months = Math.floor(days / 30);
    return `${months} ${months === 1 ? 'month' : 'months'} ago`;
  }
  const years = Math.floor(days / 365);
  return `${years} ${years === 1 ? 'year' : 'years'} ago`;
};

// Short tz abbreviation for the current browser — "BST" / "GMT" / "EST"
// etc. Used to label datetime inputs on AddBrief / AmendBriefPanel so
// the staffer typing "10:00" can see which clock they're picking from.
// (Closes the loop on the rig-time mismatch root cause — without a
// label the author and supplier can disagree silently about whether
// 10:00 means local or UTC.) Falls back to "local" on the rare browser
// that doesn't expose timeZoneName.
export const localTimezoneAbbr = () => {
  try {
    const parts = new Intl.DateTimeFormat(undefined, { timeZoneName: 'short' }).formatToParts(new Date());
    const tz = parts.find(p => p.type === 'timeZoneName');
    return tz?.value || 'local';
  } catch {
    return 'local';
  }
};

// Working-day arithmetic (Mon–Fri; weekends skipped). No public-holiday
// calendar — "working day" here means a weekday, which is all the
// question-deadline default needs. Returns a NEW Date with the input's
// time-of-day preserved. Used for the "questions close = 3 working days
// before the response deadline" default on the supplier-questions feature.
export const subtractWorkingDays = (date, n) => {
  const d = new Date(date);
  if (isNaN(d.getTime())) return null;
  let remaining = Math.max(0, Math.floor(n));
  while (remaining > 0) {
    d.setDate(d.getDate() - 1);
    const day = d.getDay(); // 0 Sun … 6 Sat
    if (day !== 0 && day !== 6) remaining -= 1;
  }
  return d;
};

// Whole calendar days a deadline is past (positive = overdue), or null if no
// deadline. ONE source so every "overdue by N" / "deadline passed N days ago"
// on a page agrees — the rail was showing 34 and 35 for the same fact because
// two callers rounded differently (post-deadline tweak §7).
export const daysPastDeadline = (deadlineIso) => {
  if (!deadlineIso) return null;
  const deadline = new Date(deadlineIso);
  if (isNaN(deadline.getTime())) return null;
  const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  return Math.round((startOfDay(new Date()) - startOfDay(deadline)) / 86400000);
};

// Working days FORWARD (Mon–Fri; weekends skipped). Returns a new Date with the
// input's time-of-day preserved. Used for the post-deadline update window
// ("3 working days after the response deadline").
export const addWorkingDays = (date, n) => {
  const d = new Date(date);
  if (isNaN(d.getTime())) return null;
  let remaining = Math.max(0, Math.floor(n));
  while (remaining > 0) {
    d.setDate(d.getDate() + 1);
    const day = d.getDay();
    if (day !== 0 && day !== 6) remaining -= 1;
  }
  return d;
};

// The default "questions close" moment for a brief: 3 working days before the
// effective response deadline. Returns an ISO string, or null if no deadline.
// Callers may override (the field is editable per brief).
export const defaultQuestionsClose = (responseDeadline) => {
  if (!responseDeadline) return null;
  const d = subtractWorkingDays(responseDeadline, 3);
  return d ? d.toISOString() : null;
};
